import React from 'react';
import { Notification, NotificationType, NotificationStatus } from '../types/notification.types';
import { cn } from '@/shared/utils/cn';
import { 
  CheckCircle, 
  AlertCircle, 
  Info, 
  AlertTriangle, 
  X,
  Clock,
  ExternalLink,
  Eye,
  EyeOff,
} from 'lucide-react';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onMarkAsUnread?: (id: string) => void;
  onDelete?: (id: string) => void;
  onNavigate?: (notification: Notification) => void;
  className?: string;
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case NotificationType.SUCCESS:
    case NotificationType.QUOTE_APPROVED:
    case NotificationType.LEAD_QUALIFIED:
    case NotificationType.SALE_COMPLETED:
    case NotificationType.COMMISSION_EARNED:
      return <CheckCircle className="h-4 w-4 text-green-500" />;
      
    case NotificationType.ERROR:
    case NotificationType.QUOTE_REJECTED:
    case NotificationType.APPOINTMENT_CANCELLED:
      return <AlertCircle className="h-4 w-4 text-red-500" />;
      
    case NotificationType.WARNING:
    case NotificationType.SYSTEM_MAINTENANCE:
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      
    case NotificationType.INFO:
    case NotificationType.PROPERTY_ADDED:
    case NotificationType.PROPERTY_UPDATED:
    case NotificationType.QUOTE_CREATED:
    case NotificationType.APPOINTMENT_SCHEDULED:
    default:
      return <Info className="h-4 w-4 text-blue-500" />;
  }
};

const getNotificationColor = (type: NotificationType) => {
  switch (type) {
    case NotificationType.SUCCESS:
    case NotificationType.QUOTE_APPROVED:
    case NotificationType.LEAD_QUALIFIED:
    case NotificationType.SALE_COMPLETED:
    case NotificationType.COMMISSION_EARNED:
      return 'bg-green-50 border-green-200';
      
    case NotificationType.ERROR:
    case NotificationType.QUOTE_REJECTED:
    case NotificationType.APPOINTMENT_CANCELLED:
      return 'bg-red-50 border-red-200';
      
    case NotificationType.WARNING:
    case NotificationType.SYSTEM_MAINTENANCE:
      return 'bg-yellow-50 border-yellow-200';
      
    case NotificationType.INFO:
    case NotificationType.PROPERTY_ADDED:
    case NotificationType.PROPERTY_UPDATED:
    case NotificationType.QUOTE_CREATED:
    case NotificationType.APPOINTMENT_SCHEDULED:
    default:
      return 'bg-blue-50 border-blue-200';
  }
};

const getTypeLabel = (type: NotificationType): string => {
  switch (type) {
    case NotificationType.PROPERTY_ADDED:
      return 'Nueva Propiedad';
    case NotificationType.PROPERTY_UPDATED:
      return 'Propiedad Actualizada';
    case NotificationType.QUOTE_CREATED:
      return 'Nueva Cotización';
    case NotificationType.QUOTE_APPROVED:
      return 'Cotización Aprobada';
    case NotificationType.QUOTE_REJECTED:
      return 'Cotización Rechazada';
    case NotificationType.LEAD_QUALIFIED:
      return 'Lead Calificado';
    case NotificationType.APPOINTMENT_SCHEDULED:
      return 'Cita Programada';
    case NotificationType.APPOINTMENT_CANCELLED:
      return 'Cita Cancelada';
    case NotificationType.SALE_COMPLETED:
      return 'Venta Completada';
    case NotificationType.COMMISSION_EARNED:
      return 'Comisión Ganada';
    case NotificationType.SYSTEM_MAINTENANCE:
      return 'Mantenimiento del Sistema';
    default:
      return 'Notificación';
  }
};

const formatTimeAgo = (date: string | Date): string => {
  const now = new Date();
  const dateObj = typeof date === 'string' ? new Date(date) : date; // ✅ Convertir string a Date
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Hace un momento';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `Hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `Hace ${hours} hora${hours > 1 ? 's' : ''}`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `Hace ${days} día${days > 1 ? 's' : ''}`;
  } else {
    return typeof date === 'string' 
      ? new Date(date).toLocaleDateString('es-DO')
      : date.toLocaleDateString('es-DO');
  }
};

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onMarkAsUnread,
  onDelete,
  onNavigate,
  className,
}) => {
  const isUnread = notification.status === NotificationStatus.UNREAD;
  const colorClass = getNotificationColor(notification.type);
  const typeLabel = getTypeLabel(notification.type);

  const handleClick = () => {
    // Solo navegar, no marcar como leído automáticamente
    if (onNavigate) {
      onNavigate(notification);
    }
  };

  const handleMarkAsUnread = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkAsUnread) {
      onMarkAsUnread(notification.id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(notification.id);
    }
  };

  return (
    <div
      className={cn(
        'p-3 border-l-4 rounded-r-lg cursor-pointer transition-all duration-200',
        isUnread ? colorClass : 'bg-transparent border-gray-200',
        isUnread && 'shadow-sm',
        className
      )}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {/* Icono condensado */}
          <div className="flex-shrink-0 mt-0.5">
            <div className="p-1.5 rounded-full bg-white shadow-sm">
              {getNotificationIcon(notification.type)}
            </div>
          </div>
          
          {/* Contenido condensado */}
          <div className="flex-1 min-w-0 space-y-1.5">
            {/* Header con tipo y estado */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                  {typeLabel}
                </span>
                {isUnread && (
                  <div className="h-1.5 w-1.5 bg-blue-500 rounded-full animate-pulse"></div>
                )}
              </div>
            </div>
            
            {/* Título condensado */}
            <h4 className="text-sm font-semibold text-gray-900 leading-tight">
              {notification.title}
            </h4>
            
            {/* Mensaje condensado */}
            <p className="text-sm text-gray-600 leading-snug">
              {notification.message}
            </p>
            
            {/* Footer condensado */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center space-x-1.5">
                <Clock className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-500">
                  {formatTimeAgo(notification.createdAt)}
                </span>
              </div>
              
              {notification.entityType && notification.entityId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNavigate) {
                      onNavigate(notification);
                    }
                  }}
                  className="flex items-center space-x-1 text-xs text-blue-600 font-medium"
                >
                  <ExternalLink className="h-3 w-3" />
                  <span>Ver detalles</span>
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Botones de acción */}
        <div className="flex items-center space-x-1">
          {/* Botón marcar como leído (solo para notificaciones no leídas) */}
          {isUnread && onMarkAsRead && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead(notification.id);
              }}
              className="flex-shrink-0 p-1.5 text-gray-400 rounded-lg"
              title="Marcar como leído"
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
          )}
          
          {/* Botón marcar como no leído (solo para notificaciones leídas) */}
          {!isUnread && onMarkAsUnread && (
            <button
              onClick={handleMarkAsUnread}
              className="flex-shrink-0 p-1.5 text-gray-400 rounded-lg"
              title="Marcar como no leído"
            >
              <EyeOff className="h-3.5 w-3.5" />
            </button>
          )}
          
          {/* Botón eliminar */}
          <button
            onClick={handleDelete}
            className="flex-shrink-0 p-1.5 text-gray-400 rounded-lg"
            title="Eliminar notificación"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
